import React, { useContext, useEffect, useRef, useState, createContext } from "react";
import {
  Box, Popper, useMediaQuery, Typography, TextField, CircularProgress, Container,
  InputAdornment,
} from '@mui/material';
// 1. Import createFilterOptions
import Autocomplete, { autocompleteClasses, createFilterOptions } from '@mui/material/Autocomplete';
import { useTheme, styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { dataConstants } from '../../src/constants';
import { useRouter } from 'next/router';
import Funds from '../../src/components/Funds';
import { VariableSizeList } from 'react-window';
import getFundSummary from "../../lib/fund/_getFundSummary";
import getPageMetaTags from '../../lib/meta/_getMetaTags';
import * as send from '../../lib/analytics/send';
import getStockIdeas from "../../lib/stock-ideas/_getStockIdeas";
import matter from "gray-matter";
import { getHeroContent, searchFund, sortByDate } from "../../src/utils/helpers";
import StockIdeasAdvertisement from "../../src/components/StockIdeasAdvertisement";
import getStockTabs from "../../lib/stock/_getStockTabsData";
import GrufityModel from "../../src/components/LandingPage/GrufityModel";
import getTickerTextData from "../../lib/stock/_getTickerTextData";
import { colors, radii, shadows } from '../../src/utils/designTokens';

const LISTBOX_PADDING = 8;

function SearchResultRow(props) {
  const { data, index, style } = props;
  const child = data[index];

  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  if (React.isValidElement(child)) {
    return React.cloneElement(child, {
      style: {
        ...child.props.style,
        ...inlineStyle,
      },
    });
  }

  return null;
}

const OuterElementContext = createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const itemCount = itemData.length;
  const itemSize = smUp ? 44 : 52;

  const getChildSize = (child) => {
    if (React.isValidElement(child) && child.props['data-group']) return 36;
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) return 8 * itemSize;
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {SearchResultRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const SearchPopper = styled(Popper)({
  [`& .${autocompleteClasses.paper}`]: {
    borderRadius: radii.md,
    boxShadow: shadows.lg,
    border: `1px solid ${colors.neutral[200]}`,
    overflow: 'hidden',
    marginTop: 8,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    padding: 0,
    '& ul': { padding: 0, margin: 0 },
  },
});

// 2. Define the highly optimized filter outside the component
const optimizedFilter = createFilterOptions({
  matchFrom: 'any',
  limit: 200, // Caps results to prevent rendering thousands of items
  stringify: (option) => option.n, // Tells MUI what string to match against
});

const FundsPage = ({ fund_summary, themeMode, isPremium, openDialog, posts, pageLoading, hero, stockideas_ads_settings, modelData }) => {
  const { getFundTabs } = fund_summary || {};
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const router = useRouter();
  const loading = open && options.length === 0;

  useEffect(() => {
    let active = true;
    if (!loading) return;
    (async () => {
      const fundSearch_jsonData = await searchFund({
        url: `/api/fund-search`,
        searchString: "",
      }) || [];
      if (active) setOptions([...fundSearch_jsonData]);
    })();
    return () => { active = false; };
  }, [loading]);

  useEffect(() => {
    if (!open) setOptions([]);
  }, [open]);

  return (
    <Box component="div">

      {/* ── Hero / Search Section ── */}
      <Box
        sx={{
          bgcolor: colors.neutral[0],
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(ellipse at 50% 0%, ${colors.accent[200]}66 0%, transparent 65%)`,
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              maxWidth: 560,
              mx: 'auto',
              pt: { xs: 5, md: 7 },
              flexDirection: 'column',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h1" component="h1" sx={{ mb: 1, fontSize: '1.875rem !important' }}>
                {hero?.h1 ?? `Shadow Hedge Funds`}
              </Typography>
            </Box>

            {/* ── Search Bar ── */}
            <Box
              component="form"
              onSubmit={(e) => e.preventDefault()}
              sx={{
                width: '100%',
                position: 'relative',
                zIndex: open ? (theme) => theme.zIndex.appBar - 1 : 0,
              }}
            >
              <Autocomplete
                id="search-funds"
                autoHighlight
                disableListWrap
                PopperComponent={SearchPopper}
                ListboxComponent={ListboxComponent}
                sx={{ width: '100%' }}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                onChange={(event, newValue) => {
                  setValue(newValue);
                  if (router && newValue && newValue.f) {
                    router.push(`/funds/${newValue.f}`);
                  }
                  send.uevent(event, `fund-home-search ${newValue?.n} (${newValue?.f})`);
                }}
                isOptionEqualToValue={(option, value) => option.n === value.n}

                // 3. Utilize the optimized filter function here
                filterOptions={(options, params) => {
                  if (params.inputValue === "") return [];
                  return optimizedFilter(options, params);
                }}

                options={options}
                loading={loading}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return '';
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for Berkshire Hathaway, Vanguard..."
                    variant="outlined"
                    hiddenLabel
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 0.5 }}>
                          {loading ? (
                            <CircularProgress size={20} thickness={2.5} sx={{ color: colors.accent.main }} />
                          ) : (
                            <SearchIcon sx={{ color: colors.neutral[400], fontSize: 22 }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 52,
                        borderRadius: `${radii.xl}px`,
                        bgcolor: colors.neutral[0],
                        fontSize: { xs: '16px', sm: '0.95rem' },
                        boxShadow: shadows.input,
                        transition: 'box-shadow 200ms ease, border-color 200ms ease',
                        '& fieldset': {
                          borderColor: colors.neutral[200],
                          transition: 'border-color 200ms ease',
                        },
                        '&:hover fieldset': {
                          borderColor: colors.neutral[300],
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.accent.main,
                          borderWidth: '1.5px',
                        },
                        '&.Mui-focused': {
                          boxShadow: shadows.inputFocus,
                        },
                        '&.Mui-focused .MuiInputAdornment-root svg': {
                          color: colors.accent.main,
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 0,
                        '&::placeholder': {
                          color: colors.neutral[400],
                          opacity: 1,
                        },
                      },
                    }}
                  />
                )}

                renderGroup={(params) => [
                  <Box
                    key={params.key}
                    component="li"
                    data-group="true"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: colors.neutral[500],
                      px: 2,
                      display: 'flex',
                      alignItems: 'flex-end',
                      pb: 0.5,
                      bgcolor: colors.neutral[0],
                      borderBottom: `1px solid ${colors.neutral[100]}`,
                    }}
                  >
                    {params.group}
                  </Box>,
                  params.children
                ]}

                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Typography
                      key={key}
                      component="li"
                      {...optionProps}
                      noWrap
                      title={option?.n}
                      sx={{
                        fontSize: '0.875rem',
                        px: 2,
                        color: colors.neutral[700],
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'background-color 120ms ease, color 120ms ease',
                        '&:hover, &.Mui-focused': {
                          bgcolor: colors.neutral[50],
                          color: colors.neutral[900],
                        },
                        '&[aria-selected="true"]': {
                          bgcolor: colors.accent[50],
                          color: colors.accent.dark,
                        },
                      }}
                    >
                      {option?.n}
                    </Typography>
                  );
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>
      {modelData && modelData.length > 0 && (
        <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 } }}>
          <GrufityModel modelData={modelData} />
        </Container>
      )}
      {posts && posts.length > 0 && (
        <Container maxWidth="xl" sx={{ pt: 3 }}>
          <StockIdeasAdvertisement
            stock_ideas={posts}
            positionBottom={true}
            pageLoading={pageLoading}
            ad_settings={stockideas_ads_settings}
            tab="fund"
          />
        </Container>
      )}
      <Container maxWidth="xl">
        <Funds
          data={getFundTabs}
          themeMode={themeMode}
          isPremium={isPremium}
          openDialog={openDialog}
        />
      </Container>
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" component="h2" sx={{ mb: 1.5 }}>
            The stock market is a vast ocean and navigating it is not easy.
          </Typography>
          <Typography variant="subtitle1" component="p" sx={{ mb: 4 }}>
            However, there are lighthouses to guide you — the informed decisions of professional fund managers.
          </Typography>
          <Typography variant="body1" component="div" sx={{ mb: 5, color: 'text.description', lineHeight: 1.8 }}>
            These experts manage billions of dollars and dedicate their careers to researching and identifying promising investments.
            <br /><br />
            <strong>Wouldn't you want to know which stocks are catching their eye?</strong>
          </Typography>

          <Box
            sx={{
              textAlign: 'left',
              p: { xs: 3, md: 5 },
              bgcolor: '#ffffff',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.200',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            }}
          >
            <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
              Studying the stock holdings of popular fund managers has many benefits:
            </Typography>
            {[
              'You gain valuable perspective on the market and potential growth sectors.',
              "You discover hidden gems that haven't yet reached their full potential and are unknown to the broader market.",
              'Shadow investing alongside experienced fund managers can help create a well-rounded portfolio.',
            ].map((benefit, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'secondary.main',
                    mt: 1,
                    mr: 2,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 6 }}>
            <Typography variant="body1" component="div" sx={{ mb: 1.5, color: 'text.primary' }}>
              <strong>Why go it alone? Let the wisdom of fund managers be your guide.</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.description', mb: 1 }}>
              Grufity covers all hedge funds, big or small. Holdings are kept updated and quick visual analysis is provided for all stocks in the portfolio.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              <strong>Save days of research by browsing through your favorite funds.</strong>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export async function getStaticProps({ preview = false, type = "fund", limit = 16 }) {
  const fund_summary = (await getFundSummary(preview)) || []
  const { getStockTabs: _Ads } = (await getStockTabs("AAPL", "ADS", preview)) || [];
  let stock_ideas_ads = _Ads && _Ads?.length > 0 ? _Ads?.find(d => d?.section_code === "stock_ideas_reports") : {}
  stock_ideas_ads = stock_ideas_ads && stock_ideas_ads?.settings ? stock_ideas_ads?.settings : ""
  const fund_news = []
  const meta_tags = (await getPageMetaTags(`/funds`, false)) || {}
  const { getMetaTags } = meta_tags || {}
  const { getBlogs: stock_ideas } = (await getStockIdeas()) || {}
  const posts = stock_ideas?.map((blog) => {
    const { data: frontmatter } = matter(blog?.content)
    return { slug: blog?.urlsuffix, frontmatter, }
  })
  const hero_data = (await getStockTabs("AAPL", "HERO")) || [];
  const { getStockTabs: hero } = hero_data || {};

  const { getTickerTextData: rawModelData } = (await getTickerTextData("FUNDSMODEL")) || {}

  const modelData = rawModelData?.map(item => {
    try {
      const parsedValue = JSON.parse(item.v);
      return {
        ...item,
        v: parsedValue
      };
    } catch (e) {
      console.error('Error parsing model data:', e);
      return item;
    }
  }) || [];

  return {
    props: {
      fund_summary,
      fund_news,
      posts: posts?.sort(sortByDate) ?? [],
      hero: getHeroContent(hero, "funds") || {},
      stockideas_ads_settings: stock_ideas_ads || {},
      meta_tags: getMetaTags?.tags || null,
      modelData: modelData || []
    },
    revalidate: dataConstants.REVALIDATE_IN.ONE_MONTH,
  }
}

export default FundsPage;
